import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosService, useUser } from 'shared/helpers/axios';
import { useTheme } from '@mui/material/styles';


const SendMessagePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('user');
  
  // Get current user from context
  const { user: currentUser } = useUser();
  const consortiumId = searchParams.get('consortium');
  // A message to a lab goes to its PI (?lab=<id>)
  const labId = searchParams.get('lab');
  const [labSubject, setLabSubject] = useState(null);

  const [recipient, setRecipient] = useState(null);
  const [recipientActorType, setRecipientActorType] = useState(null); // 'user', 'consortium', 'organization'
  const [recipientEntityId, setRecipientEntityId] = useState(null); // The entity ID
  const [messageBody, setMessageBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!recipientId && !consortiumId && !labId) {
      setError('No recipient specified');
      return;
    }
    
    if (labId) {
      fetchLabInfo();
    } else if (consortiumId) {
      fetchConsortiumInfo();
    } else if (recipientId) {
      fetchRecipientInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId, consortiumId, labId]);

  const fetchLabInfo = async () => {
    setLoading(true);
    try {
      const { data: lab } = await axiosService.get(`/consortium/labs/${labId}/`);
      setRecipientActorType('user');
      setRecipientEntityId(lab.pi);
      setLabSubject(`Message to ${lab.name}`);
      setRecipient({
        id: lab.id,
        name: lab.name,
        avatar: lab.cover_url || null,
        position: `Lab · received by its PI, ${lab.pi_detail?.name || ''}`,
        email: '',
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching lab info:', err);
      setError('Failed to load lab information');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsortiumInfo = async () => {
    setLoading(true);
    try {
      const response = await axiosService.get(`/consortium/consortiums/${consortiumId}/`);
      const consortiumData = response.data;
      
      // Set actor type and entity ID for consortium
      setRecipientActorType('consortium');
      setRecipientEntityId(consortiumData.id);
      
      // Display consortium info
      setRecipient({
        id: consortiumData.id,
        name: consortiumData.name,
        avatar: consortiumData.logo || null,
        position: 'Consortium',
        email: consortiumData.coordinator_member?.email || '',
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching consortium info:', err);
      setError('Failed to load consortium information');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipientInfo = async () => {
    setLoading(true);
    try {
      const response = await axiosService.get(`/users/${recipientId}/`);
      
      // Set actor type and entity ID for user
      setRecipientActorType('user');
      setRecipientEntityId(recipientId);
      
      setRecipient(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching recipient:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          'Failed to load recipient information';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageBody.trim()) {
      setError('Message body cannot be empty');
      return;
    }

    if (!recipientActorType || !recipientEntityId) {
      setError('No valid recipient found');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Use current user from context (already fetched by UserProvider)
      // No need to fetch again - reduces API calls
      const currentUserId = currentUser?.id;
      
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }

      await axiosService.post('/interact/messages/', {
        sender_actor_type: 'user',
        sender_entity_id: currentUserId,
        recipient_actor_type: recipientActorType,
        recipient_entity_id: recipientEntityId,
        body: messageBody.trim(),
        ...(labSubject ? { subject: labSubject } : {}),
      });

      setSuccess(true);
      setMessageBody('');

      // Navigate to messages page after 1.5 seconds
      setTimeout(() => {
        navigate(`/messages/`);
      }, 1500);
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!recipientId && !consortiumId && !labId) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Invalid recipient. Please go back and try again.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!recipient) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Recipient information not loaded. Please go back and try again.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={600}>
          Send Message
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Message sent successfully! Redirecting to conversation...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Recipient Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={recipient.avatar}
              sx={{ width: 56, height: 56, fontSize: '2rem',
                backgroundColor: theme.palette.primary.avatar2,
                color: 'white'

               }}
            >
              {recipient.name?.charAt(0)?.toUpperCase() || 
               recipient.first_name?.charAt(0)?.toUpperCase() || 
               'U'}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {recipient.name || `${recipient.first_name} ${recipient.last_name}`}
              </Typography>
              {recipient.position && (
                <Typography variant="body2" color="text.secondary">
                  {recipient.position}
                </Typography>
              )}
              {recipient.email && (
                <Typography variant="caption" color="text.secondary">
                  {recipient.email}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Message Form */}
      <Paper sx={{ p: 1, mb:2, pb:3}}>
        <Typography variant="h6" gutterBottom>
          New Message
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Message Body */}
          <TextField
            fullWidth
            multiline
            rows={10}
            label="Message"
            placeholder="Type your message here..."
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending || success}
            required
            helperText="Press Ctrl+Enter to send"
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={sending || success}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendMessage}
              disabled={!messageBody.trim() || sending || success}
            >
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Help Text */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Your message will be sent directly to {recipient.name || `${recipient.first_name} ${recipient.last_name}`}.
          They will receive a notification and can reply to your message.
        </Typography>
      </Box>
    </Container>
  );
};

export default SendMessagePage;
